/**
 * What a layout is, as data. A preset is a tree of boxes on the 1080×1920 canvas: a `split` stacks its
 * children in a row or a column, a `slot` holds one module. Sizes are fixed pixels or a share (`fr`) of
 * what the fixed ones leave — the same rule as CSS flex, so the geometry we compute is the one drawn.
 *
 * Presets are validated with these schemas when they are loaded, whether written by hand or exported
 * from the layout editor: a broken preset is an error on screen, never a blank region.
 */
import { z } from 'zod';
import { partialAppearanceSchema, type Appearance } from '../core/appearance';

export const CANVAS = { width: 1080, height: 1920 } as const;

export const sizeSchema = z.union([
  z.object({ px: z.number().nonnegative() }).strict(),
  z.object({ fr: z.number().positive() }).strict(),
]);
export type Size = z.infer<typeof sizeSchema>;

/** `pin: 'bottom'` keeps a band last whatever a constraint reorders (the status bar). */
export type Pin = 'bottom';

export interface SlotNode {
  kind: 'slot';
  module: string;
  size?: Size;
  pin?: Pin;
}

export interface SplitNode {
  kind: 'split';
  id: string;
  direction: 'row' | 'column';
  size?: Size;
  pin?: Pin;
  children: LayoutNode[];
}

export type LayoutNode = SlotNode | SplitNode;

const slotSchema = z.object({
  kind: z.literal('slot'),
  module: z.string().min(1),
  size: sizeSchema.optional(),
  pin: z.literal('bottom').optional(),
}).strict();

export const layoutNodeSchema: z.ZodType<LayoutNode> = z.lazy(() => z.union([
  slotSchema,
  z.object({
    kind: z.literal('split'),
    id: z.string().min(1),
    direction: z.enum(['row', 'column']),
    size: sizeSchema.optional(),
    pin: z.literal('bottom').optional(),
    children: z.array(layoutNodeSchema),
  }).strict(),
]));

export const transitionSchema = z.enum(['morph', 'fade', 'none']);
export type Transition = z.infer<typeof transitionSchema>;

export const presetSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  /** i18n key of the name shown in pickers. */
  label: z.string().min(1),
  /** Icon name (ui-kit/Icon) shown with the name in Settings. */
  icon: z.string().optional(),
  /** The scene behind the regions: background plus avatar (see shell/scenes). */
  scene: z.string().min(1),
  /**
   * The stage moved down, background and avatar together (the avatar stands in its place in the picture), for a
   * layout whose chat and avatar sit lower than the kiosk's. Above it, the picture's own headroom when it is taller
   * than the canvas, else a blur of it (Shell.tsx): never a bare band.
   */
  stageOffsetY: z.number().min(0).optional(),
  transition: transitionSchema.default('morph'),
  /** How this UI looks beyond the layout (chat alignment and size, margins); Settings can change it. */
  appearance: partialAppearanceSchema.optional(),
  root: layoutNodeSchema,
}).strict().superRefine((preset, ctx) => {
  if (preset.root.kind !== 'split') {
    ctx.addIssue({ code: 'custom', message: 'root must be a split', path: ['root'] });
    return;
  }
  const seen = new Set<string>();
  for (const m of modulesOf(preset.root)) {
    if (seen.has(m)) ctx.addIssue({ code: 'custom', message: `module "${m}" is placed twice`, path: ['root'] });
    seen.add(m);
  }
});

export interface Preset {
  id: string;
  label: string;
  icon?: string;
  scene: string;
  stageOffsetY?: number;
  transition: Transition;
  appearance?: Partial<Appearance>;
  root: SplitNode;
}

/** Parse untrusted preset data (a file, the editor) into a Preset, or the list of what is wrong with it. */
export function parsePreset(data: unknown): { ok: true; preset: Preset } | { ok: false; errors: string[] } {
  const r = presetSchema.safeParse(data);
  if (!r.success) {
    return { ok: false, errors: r.error.issues.map((i) => `${i.path.join('.') || '(preset)'}: ${i.message}`) };
  }
  return { ok: true, preset: r.data as Preset };
}

/** The modules a tree places, in document order. */
export function modulesOf(node: LayoutNode): string[] {
  return node.kind === 'slot' ? [node.module] : node.children.flatMap(modulesOf);
}
